import { Component, computed, inject, Type } from '@angular/core';
import { MDIconComponent, MDHeaderComponent, MDDialogService, MDContentBoxComponent } from '@melodic-ui/components';
import { IndicatorType } from '@melodic-ui/types';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { GradeTagComponent } from '../../grade-tag/grade-tag.component';
import { GradingTemplateCategoryType } from '@lb/api/core/interfaces';
import { take } from 'rxjs';
import { LessonDetailFormStateService } from '../lesson-detail-form-state.service';
import { IProjectGradeResult, SemesterProjectGradeComponent } from './semester-project-grade/semester-project-grade.component';
import { ViewRubricDialogComponent, toProjectRubricDialogInputs } from '../view-rubric-dialog/view-rubric-dialog.component';

@Component({
	selector: 'lb-lesson-detail-project-final',
	templateUrl: './lesson-detail-project-final.component.html',
	styleUrl: './lesson-detail-project-final.component.scss',
	imports: [MDIconComponent, MDHeaderComponent, GradeTagComponent, MDContentBoxComponent],
	standalone: true
})
export class LessonDetailProjectFinalComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);
	private readonly dialogService = inject(MDDialogService);

	// `!isReadOnly()` already implies the user is an instructor/admin on an editable (non-finalized) semester.
	canGrade = computed(() => this.formState.canSave() && !this.formState.isReadOnly());
	isProjectFinalGradeInvalid = () => this.formState.isProjectFinalGradeInvalid();

	readonly projectCategory = computed(() => this.state.gradingTemplateCategories().find((c) => c.categoryType === GradingTemplateCategoryType.Project));
	readonly projectCriteria = computed(() => {
		return [...(this.projectCategory()?.criteria ?? [])].sort((a, b) => a.orderNumber - b.orderNumber);
	});

	readonly criteriaGradeMap = computed(() => {
		const criteriaGrades = this.formState.projectCriteriaGrades();
		const grades = this.state.grades();
		const map: Record<number, { gradeName: string; color: IndicatorType } | null> = {};
		for (const [criteriaID, gradeID] of Object.entries(criteriaGrades)) {
			if (gradeID != null) {
				const grade = grades.find((g) => g.gradeID === gradeID);
				map[Number(criteriaID)] = grade ? { gradeName: grade.gradeName, color: (grade.color as IndicatorType) ?? 'default' } : null;
			} else {
				map[Number(criteriaID)] = null;
			}
		}
		return map;
	});

	readonly overallGrade = computed(() => {
		const gradeID = this.formState.projectFinalGradeID();
		if (gradeID == null) return null;
		return this.state.grades().find((g) => g.gradeID === gradeID) ?? null;
	});

	readonly overallGradeColor = computed<IndicatorType>(() => {
		return (this.overallGrade()?.color as IndicatorType) ?? 'default';
	});

	gradeButtonText = computed(() => {
		const finalGradeID = this.formState.projectFinalGradeID();
		return finalGradeID == null ? 'Assign Grades' : 'Edit Grades';
	});

	/**
	 * Read-only rubric reference for the project criteria. On the final lesson this block replaces the
	 * project-progress block, so without it a learner (or an instructor on a locked semester) would have
	 * no way to see what the criteria grades mean — the grading dialog shows the rubric, but only to
	 * whoever can grade.
	 */
	openRubricDialog(): void {
		this.dialogService.open(ViewRubricDialogComponent as Type<any>, toProjectRubricDialogInputs(this.projectCategory()));
	}

	openProjectGradeDialog(): void {
		const projectCategory = this.projectCategory();
		if (!projectCategory) {
			return;
		}

		const dialogRef = this.dialogService.open(SemesterProjectGradeComponent as Type<Component>, {
			grades: this.state.grades(),
			projectCategory: this.projectCategory(),
			initialCriteriaGrades: this.formState.projectCriteriaGrades()
		});

		dialogRef.afterClosed.pipe(take(1)).subscribe({
			next: (result) => {
				if (result == null) {
					return;
				}

				const gradeResult = result as IProjectGradeResult;
				this.formState.setProjectGradeResult(gradeResult);
			}
		});
	}
}
